import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../AuthContext';
import togeelogo from '../assets/images/togee-logo.png';
import togeeMoodMobil from '../assets/images/togee-mood-mobil.png';
import togeeMotion from '../assets/images/togee-motion.jpeg';
import deFlag from '../assets/images/de.png';
import enFlag from '../assets/images/en.png';

const Login = () => {
  const { t, i18n } = useTranslation();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: ''
  });
  const [isLogin, setIsLogin] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  
  const { login, register, error, clearError } = useAuth();

  useEffect(() => {
    clearError();
  }, [clearError]);

  // Add login-page class to body when component mounts
  useEffect(() => {
    document.body.classList.add('login-page');
    return () => {
      document.body.classList.remove('login-page');
    };
  }, []);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setSuccessMessage('');
    
    let result;
    if (isLogin) {
      result = await login(formData.email, formData.password);
    } else {
      result = await register(formData.name, formData.email, formData.password);
    }
    
    if (!result.success) {
      setIsLoading(false);
      // Error will be displayed through the error state from AuthContext
    } else if (result.requiresVerification) {
      // Show verification message
      setIsLoading(false);
      setSuccessMessage('Registration successful! Please check your email for a 4-character verification code. You can log in, but some features require email verification.');
    } else {
      // Regular success (shouldn't happen with new system, but keeping for completeness)
      setIsLoading(false);
    }
  };

  const toggleMode = () => {
    setIsLogin(!isLogin);
    setFormData({
      name: '',
      email: '',
      password: ''
    });
    clearError();
    setSuccessMessage('');
  };

  return (
    <div 
      className="login-container"
      style={{
        '--mobile-bg': `url(${togeeMoodMobil})`,
        '--desktop-bg': `url(${togeeMotion})`
      }}
    >
      <div className="login-logo">
        <img 
          src={togeelogo} 
          alt="togee" 
          className="logo-image"
          onLoad={() => console.log('Logo loaded successfully')}
          onError={(e) => {
            console.log('Logo loading failed, using fallback');
            // Fallback wenn Logo nicht gefunden wird
            e.target.style.display = 'none';
            e.target.nextSibling.style.display = 'block';
          }}
        />
        <h2 className="logo-fallback" style={{display: 'none'}}>
          {isLogin ? t('auth.loginTitle') : t('auth.registerTitle')}
        </h2>
      </div>
      
      <div className="login-card">
        <form onSubmit={handleSubmit}>
          {!isLogin && (
            <div className="form-group">
              <label htmlFor="name">{t('auth.name')}:</label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
              />
            </div>
          )}
          <div className="form-group">
            <label htmlFor="email">{t('auth.email')}:</label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
            />
          </div>
          <div className="form-group">
            <label htmlFor="password">{t('auth.password')}:</label>
            <input
              type="password"
              id="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              required
              minLength="6"
            />
          </div>
          {error && <div className="error-message">{error}</div>}
          {successMessage && <div className="success-message" style={{color: 'green', margin: '10px 0', padding: '10px', backgroundColor: '#f0f8f0', border: '1px solid #4CAF50', borderRadius: '4px'}}>{successMessage}</div>}
          <button type="submit" disabled={isLoading} className="submit-btn">
            {isLoading ? (isLogin ? t('auth.loggingIn') : t('auth.registering')) : (isLogin ? t('auth.login') : t('auth.register'))}
          </button>
        </form>
        
        {/* Language Selection */}
        <div className="language-selector">
          <div className="flag-buttons">
            <button 
              type="button" 
              className={`flag-btn ${i18n.language === 'de' ? 'active' : ''}`}
              onClick={() => i18n.changeLanguage('de')}
              title="Deutsch"
            >
              <img src={deFlag} alt="Deutsch" className="flag-icon" />
            </button>
            <button 
              type="button" 
              className={`flag-btn ${i18n.language === 'en' ? 'active' : ''}`}
              onClick={() => i18n.changeLanguage('en')}
              title="English"
            >
              <img src={enFlag} alt="English" className="flag-icon" />
            </button>
          </div>
        </div>

        <div className="auth-toggle">
          <p>
            {isLogin ? t('auth.noAccount') : t('auth.alreadyAccount')}
            <button type="button" onClick={toggleMode} className="toggle-btn">
              {isLogin ? t('auth.registerHere') : t('auth.loginHere')}
            </button>
          </p>
        </div>

        {/* FAQ Link */}
        <div className="faq-link-section">
          <Link to="/faq" className="faq-link">
            {t('faq.viewFAQ') || 'FAQ anzeigen'}
          </Link>
        </div>

        {/* Legal Links */}
        <div className="legal-links-section">
          <a href={i18n.language === 'de' ? '/impressum.html' : '/impressum-en.html'} className="legal-link">
            {i18n.language === 'de' ? 'Impressum' : 'Legal Notice'}
          </a>
          <span className="legal-separator">•</span>
          <a href={i18n.language === 'de' ? '/datenschutz.html' : '/datenschutz-en.html'} className="legal-link">
            {i18n.language === 'de' ? 'Datenschutz' : 'Privacy Policy'}
          </a>
        </div>
      </div>
    </div>
  );
};

export default Login; 